import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import usptoApi from '../../../../api/data-sections/usptoApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { UsptoGrantClaimModel, UsptoGrantModel } from '../../../../models/data-sections/uspto/grantModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface GrantModalProps {
  searchExecutionId: string;
  selected_grant: UsptoGrantModel;
}

const GrantModal: React.FC<GrantModalProps> = ({
  searchExecutionId,
  selected_grant
}) => {
  const [grantData, setGrantData] = useState<UsptoGrantModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await fetchApiGetGrantDetails(
          searchExecutionId,
          selected_grant.publication_country ?? '',
          selected_grant.publication_doc_number ?? '',
          new Date(selected_grant.__ci_system_search_date_key ?? 0)
        );
      } catch (error) {
        console.error('Error fetching grant data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchExecutionId, selected_grant]);

  const fetchApiGetGrantDetails = async (
    search_execution_id: string,
    publication_country: string,
    publication_doc_number: string,
    __ci_system_search_date_key: Date
  ): Promise<void> => {
    try {
      const response = await usptoApi.getGrantDetail(
        search_execution_id,
        publication_country,
        publication_doc_number,
        __ci_system_search_date_key
      );

      if (response.success) {
        const data = (response as ResponseSuccessModel).data as UsptoGrantModel[];
        setGrantData(data?.[0] || null);
      } else {
        handleError(
          mapResponseErrorToSystemError(response as ResponseErrorModel),
          null,
          setDidErrorOccur,
          triggerSystemError
        );
      }
    } catch (error) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        null,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1} mb={2}>
        <Typography variant="h6">
          {grantData?.invention_title || 'USPTO Grant'}
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : didErrorOccur || !grantData ? (
        <Typography color="error">Failed to load grant details.</Typography>
      ) : (
        <Box mt={2}>
          <Typography><strong>Application Date:</strong> {grantData.application_date || 'N/A'}</Typography>

          {(grantData.applicants ?? []).length > 0 && (
            <Typography>
              <strong>Applicants:</strong> {(grantData.applicants ?? []).map(a => a.orgname).filter(Boolean).join(', ')}
            </Typography>
          )}

          {grantData.abstract_text && (
            <Box mt={2}>
              <Typography variant="h6">Abstract</Typography>
              <Typography paragraph>{grantData.abstract_text.trim()}</Typography>
            </Box>
          )}

          {(grantData.claims ?? []).length > 0 && (
            <Box mt={3}>
              <Typography variant="h6">Claims</Typography>
              <Box component="ul" pl={3}>
                {(grantData.claims ?? []).flatMap((claim: UsptoGrantClaimModel) =>
                  (claim.claim_texts ?? []).map((text: string, index: number) => (
                    <li key={`${claim.id}-${index}`} style={{ marginBottom: '0.5rem' }}>
                      {text}
                    </li>
                  ))
                )}
              </Box>
            </Box>
          )}

          {(grantData.descriptions ?? []).length > 0 && (
            <Box mt={3}>
              <Typography variant="h6">Description</Typography>
              {(grantData.descriptions ?? []).map((desc, index) => (
                <Typography key={index} paragraph>{desc}</Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default GrantModal;
